import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { Section } from "../components/Section";
import { HLAResultsForm } from "../components/tests/HLAResultsForm";
import { ResultAttributesTable } from "../components/tests/ResultAttributesTable";
import { ResultOptionsTable } from "../components/tests/ResultOptionsTable";
import { ResultRangesTable } from "../components/tests/ResultRangesTable";
import { ResultSpecDetailForm } from "../components/tests/ResultSpecDetailForm";
import { useTabSearch } from "../hooks";

type PageParams = { id: number; specId: number };

export function EditTestResultSpecPage(params: PageParams) {
  const tabsProps = useTabSearch();

  const { data, isLoading } = trpc.admin.testResultSpec.get.useQuery({
    id: params.specId,
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  const resultSpec = data;

  if (!resultSpec) {
    return <Centered>Test result spec not found.</Centered>;
  }
  const testSpec = resultSpec.testSpec;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/tests" }}>Test specs</Breadcrumb>
            <Breadcrumb
              href={{
                to: "/admin/tests/$id/edit",
                params: { id: testSpec.id },
                search: { t: "results" },
              }}
            >
              {testSpec.name} ({testSpec.code})
            </Breadcrumb>
            <Breadcrumb>{resultSpec.name}</Breadcrumb>
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <ToggleEnableDialog resultSpec={resultSpec}>
              <Button variant="outline">
                {resultSpec.enabled ? "Disable" : "Enable"}
              </Button>
            </ToggleEnableDialog>
            <DeleteTestResultSpecDialog resultSpec={resultSpec}>
              <Button variant="destructive">Delete</Button>
            </DeleteTestResultSpecDialog>
          </div>
        }
      >
        <div className="flex items-center gap-2">
          {resultSpec.name || "<unknown>"}
          {resultSpec.enabled === false && <Badge>Disabled</Badge>}
        </div>
      </Section.Title>
      <Tabs {...tabsProps}>
        <TabsList className="w-full justify-start">
          <Tab id="details">Details</Tab>
          <Tab id="results">Result options</Tab>
          <Tab id="ranges">Ranges</Tab>
          <Tab id="attributes">Attributes</Tab>
        </TabsList>
        <TabPanel id="details">
          <ResultSpecDetailForm
            resultSpec={resultSpec}
            test={{ id: resultSpec.testId }}
          />
        </TabPanel>
        <TabPanel id="results">
          {resultSpec.type === "TEXT" ? (
            <ResultOptionsTable resultSpec={resultSpec} />
          ) : resultSpec.type === "HLA" ? (
            <HLAResultsForm resultSpec={resultSpec} />
          ) : (
            <Centered className="bg-muted p-5 rounded-md my-4">
              <Text muted>
                Result options not available for {resultSpec.type} result specs.
              </Text>
            </Centered>
          )}
        </TabPanel>
        <TabPanel id="ranges">
          <ResultRangesTable resultSpec={resultSpec} />
        </TabPanel>
        <TabPanel id="attributes">
          <ResultAttributesTable resultSpec={resultSpec} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function DeleteTestResultSpecDialog({
  children,
  resultSpec,
}: {
  children: ReactNode;
  resultSpec: { id: number };
}) {
  const deleteSpec = trpc.admin.testResultSpec.delete.useMutation();
  const onConfirm = () => {
    deleteSpec.mutate(
      resultSpec,
      mutationResponse({
        errorTitle: "Error deleting test result spec",
        successMessage: "Test result spec deleted",
        onSuccess() {
          history.back();
        },
      }),
    );
  };
  return (
    <ConfirmPrompt
      danger
      title="Delete test result spec"
      message="Are you sure you want to delete this test result spec?"
      onConfirm={onConfirm}
      loading={deleteSpec.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}

function ToggleEnableDialog({
  resultSpec,
  children,
}: {
  children: ReactNode;
  resultSpec: { id: number; enabled: boolean };
}) {
  const [open, setOpen] = useState(false);
  const toggleSpec = trpc.admin.testResultSpec.toggleEnabled.useMutation();

  const verb = resultSpec.enabled ? "Disable" : "Enable";
  const verbing = resultSpec.enabled ? "disabling" : "enabling";

  const onConfirm = () => {
    toggleSpec.mutate(
      { id: resultSpec.id, enabled: !resultSpec.enabled },
      mutationResponse({
        errorTitle: `Error ${verbing} test result spec`,
        successMessage: `Test result spec ${verb.toLowerCase()}d`,
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      open={open}
      onOpenChange={setOpen}
      danger={resultSpec.enabled}
      title={`${verb} test result spec`}
      message={`Are you sure you want to ${verb.toLowerCase()} this test result spec?`}
      onConfirm={onConfirm}
      loading={toggleSpec.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
